import type {
  AgenticLogicPublishGateKey,
  AgenticLogicRuleDto,
} from "@intelligems/ig-types";
import { IgAlert, IgLoading, IgTypography } from "@intelligems/ig-theme";
import type { AgenticLogicDataSurface } from "@intelligems/ig-utils";
import { Box, Stack } from "@mui/material";
import { useImmer } from "use-immer";

import { useUpdateAgenticLogicRule } from "@src/api/agentic-logic/update-agentic-logic-rule";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useIsAdvancedAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAdvancedAgenticLogicEnabled";

import { RuleSettingsFields } from "./RuleSettingsFields";
import { RuleTestStopButton } from "./RuleTestStopButton";
import { RuleVersionsTab } from "./RuleVersionsTab";
import { SavedRuleDraft } from "./SavedRuleDraft";
import { useRuleLiveness } from "./useRuleLiveness";
import { useSaveRuleSettings } from "./useSaveRuleSettings";
import { sameRuleSettings } from "./ruleSettings";
import type { AgenticLogicRuleSettings } from "@intelligems/ig-types";
import { useUnpublishedRuleChanges } from "./useUnpublishedRuleChanges";
import { CodegenWorkspace } from "@src/features/agenticCodegen/workspace/CodegenWorkspace";
import { DiscardChangesDialog } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/DiscardChangesDialog";
import { useUnsavedWorkspaceGuard } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/useUnsavedWorkspaceGuard";
import { workspaceSaveBlockReason } from "@src/features/agenticCodegen/workspace/saveAvailability";
import {
  GateProgressStrip,
  type GateStripOperation,
} from "@src/features/agenticCodegen/workspace/components/02-GateRail/GateProgressStrip";
import { RepairApprovalPanel } from "./RepairApprovalPanel";
import { RepairGiveUpPanel } from "./RepairGiveUpPanel";
import { RecentRunsPanel } from "./recent-runs/RecentRunsPanel";
import { WorkspaceActions } from "@src/features/agenticCodegen/workspace/components/WorkspaceActions";

import { useAgenticLogicDataSurface } from "../useAgenticLogicDataSurface";
import { RegenerateGraphButton } from "../graph/RegenerateGraphButton";
import { SelectionLogicTab } from "../graph/SelectionLogicTab";
import { useRegenerateGraph } from "../graph/useRegenerateGraph";
import { useEnsureRuleScenarios } from "../graph/useEnsureRuleScenarios";
import { useOrgExclusionsGraph } from "../graph/useOrgExclusionsGraph";
import { useRuleSelectionGraph } from "../graph/useRuleSelectionGraph";
import { useScenarioPreview } from "../graph/useScenarioPreview";
import { CopyRuleDialog } from "./CopyRuleDialog";
import { RuleDetailPageView } from "./RuleDetailPageView";
import { SharedRuleSaveDialog } from "./SharedRuleSaveDialog";
import { agenticDraftSource } from "./agenticApiPort";
import { priorRepairAttempts } from "./priorRepairAttempts";
import { ruleDisplayStatus } from "../rules/ruleDisplayStatus";
import {
  canOfferRepairBaselineRestore,
  didRepairStop,
  isRepairAwaitingApproval,
  publishedJobCoversCurrentDraft,
  repairStoppedGateReason,
  ruleCheckPhase,
  shouldShowRepairGiveUp,
  shouldShowRulePublish,
} from "./repairOutcome";
import { resolveRuleExitMode, type RuleExitMode } from "./ruleExitMode";
import { useRuleDetailByline } from "./useRuleDetailByline";
import { DEFAULT_RULE_DETAIL_TAB } from "./ruleDetailTabs";
import { useRuleDetailTab } from "./useRuleDetailTab";
import {
  AGENTIC_CAPABILITIES,
  AGENTIC_GATES_CONFIG,
  AGENTIC_TEST_FILES,
  agenticPublishReviewConfig,
  agenticRollbackImpactCopy,
  buildAgenticDomainConfig,
} from "./agenticDomain";
import { AgenticVersionCode } from "./AgenticVersionCode";
import { useAgenticLogicRuleWorkspace } from "./useAgenticLogicRuleWorkspace";
import { useDraftGraphPoll } from "./useDraftGraphPoll";
import { useAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useAgenticLogicSurface";
import { useSurfaceNavigate } from "@src/features/ai/pages/agentic-logic/surface/useSurfaceNavigate";
import { useSelector } from "@src/store";
import { getAtlasBuildingRuleId } from "@src/store/slices/global";

/**
 * The Agentic Logic rule workspace: the shared {@link CodegenWorkspace} shell
 * bound to this domain and rendered **inline on a route**. CSP wraps the same
 * shell in a full-page dialog; that chrome is CSP's, not the shell's.
 */
const RuleWorkspace = ({
  rule,
  dataSurface,
  datasetsFailed,
}: {
  rule: AgenticLogicRuleDto;
  dataSurface: AgenticLogicDataSurface;
  datasetsFailed: boolean;
}) => {
  const ruleId = rule.id;
  const navigate = useSurfaceNavigate();
  const surface = useAgenticLogicSurface();
  const isAdmin = useIsAdmin() === true;
  const advancedEnabled = useIsAdvancedAgenticLogicEnabled();
  const vm = useAgenticLogicRuleWorkspace(ruleId, dataSurface);
  const updateRule = useUpdateAgenticLogicRule();
  const saveSettings = useSaveRuleSettings(ruleId);
  const [settingsEdit, setSettingsEdit] = useImmer<{
    base: AgenticLogicRuleSettings;
    value: AgenticLogicRuleSettings;
  } | null>(null);
  const settings = settingsEdit?.value ?? rule.settings;
  const settingsDirty = !sameRuleSettings(settings, rule.settings);
  const settingsConflict =
    settingsDirty &&
    settingsEdit !== null &&
    !sameRuleSettings(settingsEdit.base, rule.settings);
  const isDirty = vm.isDirty || settingsDirty;
  // Is this rule in front of shoppers, and may Save therefore deploy? See
  // `publishOnSave.ts`: a rule attached to a started experience writes its
  // draft and stops, so a merchant trying a scenario cannot change a live
  // experience by reaching for the page's most obvious button.
  const { saveAction } = useRuleLiveness(ruleId);
  // The saved rule is ahead of the version that serves. Two writers open that
  // gap: Atlas, which writes the rule row from the chat and deliberately
  // stops, and — since Save stopped deploying a serving rule — the merchant's
  // own Save. Both are answered by the gate strip's Publish.
  const { hasUnpublishedChanges, isChecking, comparisonFailed } =
    useUnpublishedRuleChanges(rule);
  const [selectedPageTab, setSelectedPageTab] = useRuleDetailTab(
    DEFAULT_RULE_DETAIL_TAB,
    advancedEnabled,
    surface.tabParam
  );
  const [confirmingExit, setConfirmingExit] = useImmer<RuleExitMode | null>(
    null
  );
  const [isCopyingRule, setIsCopyingRule] = useImmer(false);
  const [isConfirmingSharedSave, setIsConfirmingSharedSave] = useImmer(false);

  const byline = useRuleDetailByline(rule);
  const sharedExperiences =
    byline.usedIn && byline.usedIn.length > 1 ? byline.usedIn : null;
  const copySource = agenticDraftSource(vm.draftSnapshot.files);
  const copyDraft =
    copySource !== null
      ? {
          source: copySource,
          userTestSuite: vm.draftSnapshot.userTestSuite,
          dependencies: vm.draftSnapshot.dependencies,
          ...(vm.draftSnapshot.generatedTestSuite === null
            ? {}
            : { generatedTestSuite: vm.draftSnapshot.generatedTestSuite }),
          ...(vm.draftSnapshot.origin === null
            ? {}
            : { origin: vm.draftSnapshot.origin }),
        }
      : undefined;

  // The Selection logic graph — see useRuleSelectionGraph. The canvas never
  // reflects the unsaved editor buffer, and the caption says which saved
  // artifact is showing.
  const { graph: selectionGraph, isPublishedLoading: isPublishedGraphLoading } =
    useRuleSelectionGraph(ruleId);
  const canvasGraph = useOrgExclusionsGraph(selectionGraph);

  // The carts Atlas offers have to cover the rule's current branches; the
  // graph is what makes them stale, so the workspace is what keeps them fresh.
  useEnsureRuleScenarios({ ruleId, graph: selectionGraph });
  const scenarioPreview = useScenarioPreview({
    ruleId,
    graph: selectionGraph,
  });

  // The admin regenerate lane. The page owns the mutation pair so the
  // canvas can shimmer while a rebuild of an EXISTING graph is in flight —
  // the button and the skeleton share one busy signal.
  const regenerateGraph = useRegenerateGraph(ruleId);

  // The open Atlas session created this rule and is still authoring it: the
  // create tool navigated here before the first draft (and its graph) exist,
  // so publish/repair signals haven't started yet. Once the repair loop stops
  // — gave up, or was stood down by the sweep after its worker died —
  // `RepairGiveUpPanel` owns the story, and a canvas still shimmering "being
  // built" under it would be a lie.
  const atlasBuildingRuleId = useSelector(getAtlasBuildingRuleId);
  const isAtlasCreationRule = atlasBuildingRuleId === ruleId;
  const repairStopped = didRepairStop(vm.repair.run);
  const showRepairGiveUp = shouldShowRepairGiveUp(
    vm.repair.run,
    vm.repair.isVersionHistoryLoading
      ? undefined
      : (vm.activeVersion?.publishedAtTs ?? null)
  );
  const canRestoreRepairBaseline = canOfferRepairBaselineRestore(
    vm.repair.run,
    vm.activeVersion?.version ?? null
  );
  // A held run is terminal too: the checks went green, the rewrite is prepared,
  // and nothing is still working on the rule — so the "being built" shimmer
  // must stand down here exactly as it does for a stopped run, and
  // `RepairApprovalPanel` owns the story instead.
  const repairAwaitingApproval =
    isRepairAwaitingApproval(vm.repair.run) &&
    (rule.state === "draft" || hasUnpublishedChanges);
  // The single derivation every surface reads, so the chip in the rules list,
  // the panel on this tab and the attach picker cannot disagree about a rule.
  const displayStatus = ruleDisplayStatus({
    rule,
    publishJob: vm.publish.job,
    repairRun: vm.repair.run,
    advancedEnabled,
    // "Ask Atlas to fix it" disables itself the instant it is clicked, so
    // without this the panel would drop the button and keep the failure copy
    // until the new job's first frame lands — the click reading as a no-op.
    deployInFlight: vm.publish.isPublishing,
  });
  const isAtlasBuildingThisRule =
    isAtlasCreationRule && !repairStopped && !repairAwaitingApproval;

  const isGraphPending =
    selectionGraph === null &&
    (vm.draft !== null ||
      isAtlasBuildingThisRule ||
      vm.publish.isPublishing ||
      vm.repair.isActive);

  // Atlas owns the source while its chat session is building this rule or its
  // repair loop is mid-fix: each saves a new draft over whatever is in the
  // editor, so the Advanced tab holds as "Atlas is updating…" until it is
  // done rather than inviting an edit the next save would overwrite.
  const isAtlasUpdatingSource = isAtlasBuildingThisRule || vm.repair.isActive;

  // The graph lands out-of-band — the chat's backend save, the repair loop's
  // regenerations, the SQS regen worker — none of which run through this
  // browser's mutations. While the canvas shows "being built", poll the draft
  // so the skeleton actually resolves.
  useDraftGraphPoll(ruleId, isGraphPending);

  // A suite gate opens its suite, which is a file in the Advanced editor; the
  // other gates have no destination beyond the tab itself.
  const openGateInEditor = (gateKey: AgenticLogicPublishGateKey) => {
    if (gateKey === AGENTIC_GATES_CONFIG.consoleGates.generatedSuite) {
      vm.editor.setActiveFile(AGENTIC_TEST_FILES.generatedFilename);
    } else if (gateKey === AGENTIC_GATES_CONFIG.consoleGates.userSuite) {
      vm.editor.setActiveFile(AGENTIC_TEST_FILES.userFilename);
    }
  };

  // The same strip renders on the other tabs (see `banners`), where opening a
  // check has to cross to the Advanced tab first. Withheld entirely when the
  // org cannot see that tab: the strip draws its per-check "Open" button from
  // the presence of this callback, and without the destination the button is
  // a dead click rather than a disabled one.
  const handleSelectGateFromOtherTab = advancedEnabled
    ? (gateKey: AgenticLogicPublishGateKey) => {
        setSelectedPageTab("advanced");
        openGateInEditor(gateKey);
      }
    : undefined;

  const config = buildAgenticDomainConfig({
    dataSurface,
    publishReview: agenticPublishReviewConfig({
      availableSecretNames: vm.availableSecretNames,
      dependencies: vm.dependencies,
      activeVersion: vm.activeVersion,
    }),
  });

  const authorName = config.chrome.aiAuthorName ?? "Atlas";

  // "Ask Atlas to fix" runs the checks again. That is the whole mechanism:
  // a failed run opens a repair run in the publish worker's terminal tail,
  // and the supervisor takes it from there.
  //
  // It used to be a tab switch and nothing else — no endpoint, no run — and
  // the chip appeared to "remove itself" because the gate rail that draws it
  // is not on the tab it switched to.
  const askAtlasToFix = vm.runTests;
  // ONE rule for both recovery affordances: they now dispatch the same action,
  // so they must be live under the same conditions.
  const operationBusy =
    vm.publish.isPublishing || vm.repair.isActive || vm.publishOutcomePending;
  const recheckDisabled = isDirty || operationBusy;

  // The gate strip describes whichever publish job the port tracks, which is
  // not the rule's own state: a rule the repair loop stopped on never shipped,
  // yet the last run beside it can still read "All N checks passed". Hand the
  // strip the contradiction so it reports the run as stopped instead of a
  // verdict — the give-up panel renders directly above it.
  // Same lifetime as the panel: a stopped run that a later publish has
  // superseded is no longer a contradiction, and neither is one the version
  // history has not yet been able to judge.
  const gateRunAbandonedReason = showRepairGiveUp
    ? repairStoppedGateReason(vm.repair.run, authorName)
    : null;

  // The rail now shows the run that shipped a live rule, not only a draft's
  // pending checks. Beside such a rule "ready to publish" describes a publish
  // that already happened, so the strip reports the run as history instead.
  const publishedCurrentDraft = publishedJobCoversCurrentDraft({
    job: vm.publishJob,
    isDirty,
    draftLastUpdateTs: vm.draft?.lastUpdateTs,
    ruleLastUpdateTs: rule.lastUpdateTs,
  });
  const gateRunDescribesLiveVersion =
    publishedCurrentDraft ||
    (rule.state !== "draft" &&
      !hasUnpublishedChanges &&
      !isChecking &&
      !comparisonFailed &&
      !isDirty &&
      vm.publish.job?.status === "succeeded");

  // The rail follows the repair loop's CURRENT job, so every attempt it has
  // already burned leaves the screen the instant the next one is enqueued —
  // and a run that converges takes its refusals with it. These rows are the
  // record: the refusing gate and its remediation, verbatim.
  const attemptsRefusedByRepair = priorRepairAttempts(
    vm.repair.run,
    vm.publish.job?.id
  );

  const checkPhase = ruleCheckPhase({
    job: vm.publishJob,
    repairRun: vm.repair.run,
    busy: operationBusy,
    held: repairAwaitingApproval,
    stopped: showRepairGiveUp,
    hasDraftChanges: hasUnpublishedChanges || isDirty,
    checksStale:
      isDirty ||
      (vm.publishJob?.finishedAtTs != null &&
        (vm.draft?.lastUpdateTs ?? 0) > vm.publishJob.finishedAtTs),
    describesPublishedVersion: gateRunDescribesLiveVersion,
  });
  const publishDisabledHint = isDirty
    ? "Save your changes before publishing."
    : saveSettings.isSaving
      ? "Wait for your changes to finish saving."
      : comparisonFailed
        ? "Reload to check the published version."
        : isChecking
          ? "Checking the saved draft…"
          : undefined;
  const checkOperation: GateStripOperation = {
    phase: checkPhase,
    checksOnly:
      vm.repair.run?.publishOnSuccess === false ||
      (!vm.repair.run && vm.publishJob?.mode === "dry_run"),
    detail:
      checkPhase === "failed" || checkPhase === "review"
        ? rule.state === "draft"
          ? "This rule has not been published."
          : "Your published rule is unchanged."
        : undefined,
    content:
      checkPhase === "review" && vm.repair.run ? (
        <RepairApprovalPanel
          embedded
          hasPublishedVersion={rule.state !== "draft"}
          ruleId={ruleId}
          run={vm.repair.run}
          authorName={authorName}
          advancedEnabled={advancedEnabled}
          isPublishing={operationBusy}
          isPublishDisabled={publishDisabledHint !== undefined}
          onPublish={vm.publishDraft}
          onOpenCodeWorkspace={() => setSelectedPageTab("advanced")}
        />
      ) : checkPhase === "failed" && showRepairGiveUp && vm.repair.run ? (
        <RepairGiveUpPanel
          embedded
          disabled={publishDisabledHint !== undefined || operationBusy}
          ruleId={ruleId}
          run={vm.repair.run}
          authorName={authorName}
          advancedEnabled={advancedEnabled}
          canRestoreBaseline={canRestoreRepairBaseline}
          dataSurface={dataSurface}
          onOpenCodeWorkspace={() => setSelectedPageTab("advanced")}
        />
      ) : undefined,
  };

  /** Save manual changes to the draft. Only creation also publishes. */
  const saveRule = () => {
    if (settingsConflict) {
      return;
    }
    void (async () => {
      if (settingsDirty) {
        await saveSettings.save(settings);
        setSettingsEdit((edit) =>
          edit && !sameRuleSettings(edit.value, settings)
            ? { ...edit, base: { ...settings } }
            : null
        );
      }
      if (saveAction === "save_and_publish") {
        vm.saveAndPublish();
      } else if (vm.isDirty) {
        vm.saveDraft();
      }
    })().catch(() => undefined);
  };

  const exitMode = resolveRuleExitMode({ isDirty });
  // Two exits, one dialog: Cancel raises the confirm itself, while any other
  // route change is intercepted by the blocker.
  const leaveGuard = useUnsavedWorkspaceGuard(exitMode !== null);
  const goBack = () => {
    if (exitMode) {
      setConfirmingExit(exitMode);
    } else {
      navigate(surface.exitPath);
    }
  };
  // A version number is the expert vocabulary; withholding it drops the
  // dialog to its version-free sentence, which says the same shopper-facing
  // thing.
  const liveLabel =
    advancedEnabled && vm.activeVersion
      ? `v${vm.activeVersion.version}`
      : undefined;

  // Nothing else must be persisted first: the rule already exists by the time
  // this route can be reached, and a rule is writable for its whole life —
  // retiring one deletes it rather than freezing it.
  const disablePersistedActions =
    vm.repair.isActive || vm.publishOutcomePending;

  // ONE judgement for whether Save is available, shared with the hint below
  // so the button and its explanation cannot disagree.
  const saveBlockReason = workspaceSaveBlockReason({
    isReadOnly: disablePersistedActions,
    isSavingDraft: vm.isSavingDraft || saveSettings.isSaving,
    isPublishing: vm.publish.isPublishing,
    isSyntaxValid: vm.editor.syntax.isValid,
    isSyntaxPending: vm.editor.syntaxPending,
  });

  // Mirrors the shell's own blockers, which the suppressed title bar used to
  // explain. Reported most-actionable-first. Saving is what deploys, so the
  // pending-edits hint names the two verbs that read the SAVED rule instead.
  // The rule's verdict is named on its own: on a test tab nothing on screen
  // shows why Save is waiting, and "checking" and "has errors" ask for
  // different things of the author.
  const resolveActionHint = (): string | undefined => {
    if (vm.publish.isPublishing) {
      return "Wait for the current run to finish.";
    }
    if (saveBlockReason === "syntax_checking") {
      return "Checking the rule's TypeScript before it can be saved.";
    }
    if (saveBlockReason === "syntax_invalid") {
      return "Fix the TypeScript errors in the rule to save it.";
    }
    if (isDirty) {
      return "Save your changes to run tests or save this rule as a new one.";
    }
    return undefined;
  };
  const actionHint = resolveActionHint();

  return (
    <>
      <CopyRuleDialog
        open={isCopyingRule}
        ruleId={ruleId}
        ruleName={vm.entityName}
        draft={copyDraft}
        onClose={() => setIsCopyingRule(false)}
        onCopied={(copyId) => {
          setIsCopyingRule(false);
          navigate(surface.rulePath(copyId));
        }}
      />
      <SharedRuleSaveDialog
        open={isConfirmingSharedSave}
        experiences={sharedExperiences ?? []}
        // The dialog states the consequence of the Save it is gating, so it
        // gets the same resolved action `saveRule` runs — a rule that is
        // serving writes a draft, and the dialog has to say that rather than
        // promise every experience below is about to change.
        saveAction={saveAction}
        isSaving={vm.isSavingDraft || vm.publish.isPublishing}
        onClose={() => setIsConfirmingSharedSave(false)}
        onSaveAsNewRule={() => {
          setIsConfirmingSharedSave(false);
          setIsCopyingRule(true);
        }}
        onSaveToAll={() => {
          setIsConfirmingSharedSave(false);
          saveRule();
        }}
      />
      <DiscardChangesDialog
        open={confirmingExit !== null || leaveGuard.isBlocked}
        mode={confirmingExit ?? exitMode ?? "discard"}
        entityLabel="rule"
        entityName={vm.entityName}
        liveLabel={liveLabel}
        onKeepEditing={() => {
          setConfirmingExit(null);
          leaveGuard.reset();
        }}
        onDiscard={() => {
          setConfirmingExit(null);
          if (leaveGuard.isBlocked) {
            leaveGuard.proceed();
          } else {
            navigate(surface.exitPath);
          }
        }}
      />
      <RuleDetailPageView
        backLabel={surface.backLabel}
        name={vm.entityName}
        description={vm.ruleDescription ?? ""}
        eligibleSurfaces={byline.eligibleSurfaces}
        usedIn={byline.usedIn}
        isRenaming={updateRule.isPending}
        isSavingDescription={updateRule.isPending}
        selectedTab={selectedPageTab}
        onSelectedTabChange={setSelectedPageTab}
        advancedEnabled={advancedEnabled}
        onRename={async (name) => {
          await updateRule.mutateAsync({
            body: { name },
            pathParams: { ruleId },
            queryParams: undefined,
          });
        }}
        onSaveDescription={async (description) => {
          await updateRule.mutateAsync({
            body: { description },
            pathParams: { ruleId },
            queryParams: undefined,
          });
        }}
        onBack={goBack}
        banners={
          <>
            {comparisonFailed ? (
              <Box sx={{ px: 2, pt: 2 }}>
                <IgAlert
                  alertKey="rule-publication-comparison-failed"
                  severity="warning"
                  title="Published version could not be checked"
                  message="Your draft is saved. Reload to check whether it differs from the published version."
                />
              </Box>
            ) : null}
            {settingsConflict ? (
              <Box sx={{ px: 2, pt: 2 }}>
                <IgAlert
                  alertKey="rule-settings-conflict"
                  severity="warning"
                  title="The saved settings changed"
                  message="Your local settings have not been saved. Reload the saved settings before editing again."
                  primaryButton={{
                    cta: "Reload settings",
                    onClick: () => setSettingsEdit(null),
                  }}
                />
              </Box>
            ) : null}
            {datasetsFailed ? (
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <IgAlert
                  alertKey="agentic-logic-datasets-unavailable"
                  severity="warning"
                  title="Dataset keys could not be loaded"
                  message="The editor is using an empty dataset list, so every sdk.getDataset() call will report an error. Your code is unaffected — reload to try again."
                />
              </Box>
            ) : null}
          </>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <RuleTestStopButton
              job={vm.publishJob}
              repairActive={vm.repair.isActive}
              isStopping={vm.isStoppingTests}
              onStop={vm.stopTests}
            />
            {isDirty ? (
              <IgTypography variant="body2">Unsaved changes</IgTypography>
            ) : null}
            <WorkspaceActions
              presentation="split"
              disabledActionHint={actionHint}
              isSaveDisabled={
                settingsConflict ||
                (!isDirty && saveAction === "save_draft_only") ||
                saveBlockReason !== null
              }
              saveAsNewRule={{
                isDisabled:
                  disablePersistedActions ||
                  vm.publish.isPublishing ||
                  copyDraft === undefined,
                onSelect: () => setIsCopyingRule(true),
              }}
              saveImpact={
                sharedExperiences
                  ? {
                      experienceCount: sharedExperiences.length,
                      onSaveToAll: saveRule,
                    }
                  : null
              }
              runTests={
                AGENTIC_CAPABILITIES.canRunGates
                  ? {
                      isDisabled:
                        disablePersistedActions ||
                        isDirty ||
                        vm.publish.isPublishing,
                    }
                  : null
              }
              unpublish={
                AGENTIC_CAPABILITIES.canUnpublish && vm.activeVersion
                  ? {
                      isDisabled:
                        disablePersistedActions ||
                        vm.publish.isPublishing ||
                        vm.isUnpublishing,
                    }
                  : null
              }
              onSave={() => {
                if (sharedExperiences) {
                  setIsConfirmingSharedSave(true);
                } else {
                  saveRule();
                }
              }}
              onRunTests={vm.runTests}
              onUnpublish={vm.unpublishEntity}
              // The button says what the buffer is, rather than staying blue
              // and reading "Save" with nothing to save. What is NOT live is
              // the callout's story, and it carries the Publish verb.
              saveState={isDirty ? "pending_edits" : "settled"}
              saveLabel={
                saveAction === "save_and_publish"
                  ? "Save and publish"
                  : isDirty
                    ? "Save draft"
                    : "Draft saved"
              }
            />
          </Stack>
        }
        selectionLogic={
          <>
            {vm.publish.job != null ||
            hasUnpublishedChanges ||
            vm.repair.run !== null ? (
              <GateProgressStrip
                gates={vm.publish.gates}
                config={config.gates}
                heading={config.chrome.gatesHeading}
                isPublishing={operationBusy}
                operation={checkOperation}
                priorAttempts={attemptsRefusedByRepair}
                onSelectGate={handleSelectGateFromOtherTab}
                publish={
                  shouldShowRulePublish({
                    operationBusy,
                    publishedCurrentDraft,
                    hasUnpublishedChanges,
                    ruleState: rule.state,
                    checkPhase,
                  })
                    ? {
                        onPublish:
                          checkOperation.checksOnly && checkPhase === "failed"
                            ? vm.runTests
                            : vm.publishDraft,
                        label:
                          checkPhase === "failed" ? "Try again" : "Publish",
                        isPublishing: operationBusy,
                        disabledHint: publishDisabledHint,
                      }
                    : undefined
                }
              />
            ) : null}
            {isDirty ? (
              <IgTypography
                color="text.secondary"
                variant="caption"
                sx={{ px: 2, pt: 1 }}
              >
                Unsaved edits are not shown. Save the draft to update this
                graph.
              </IgTypography>
            ) : null}
            <SelectionLogicTab
              graph={canvasGraph}
              isLoading={vm.isLoading || isPublishedGraphLoading}
              // A save with no graph regenerates one asynchronously, and that
              // worker runs alongside the publish. Until the publish settles, a
              // missing graph means "not built yet", not "this rule has none".
              // The repair loop keeps regenerating graph + source after a
              // failed publish, so "publishing settled" no longer means "the
              // graph is final" — pending tracks the loop too, and the open
              // Atlas session that is still authoring this rule's first draft.
              isPending={isGraphPending}
              pendingHint={
                isAtlasBuildingThisRule && !vm.publish.isPublishing
                  ? "Atlas is writing this rule now — the graph appears when its first draft is saved."
                  : undefined
              }
              // Deliberately NOT tied to the repair loop: a repair run can
              // regenerate for minutes on a slow poll, and minutes of
              // shimmer reads as broken. Only the direct regenerate lane,
              // whose round trip is one request, shimmers the canvas.
              isRebuilding={regenerateGraph.isBusy && selectionGraph !== null}
              canvasAction={
                <RegenerateGraphButton
                  canRegenerate={regenerateGraph.canRegenerate}
                  isBusy={regenerateGraph.isBusy}
                  onRegenerate={regenerateGraph.regenerate}
                />
              }
              overlaysByNodeId={scenarioPreview.overlaysByNodeId}
              overlaysByEdgeId={scenarioPreview.overlaysByEdgeId}
            />
          </>
        }
        // Manual settings are included in Save draft; publishing remains separate.
        displaySettings={
          <RuleSettingsFields
            settings={settings}
            saving={saveSettings.isSaving || vm.publish.isPublishing}
            onChange={(value) =>
              setSettingsEdit((edit) => ({
                base: edit?.base ?? { ...rule.settings },
                value: { ...value },
              }))
            }
          />
        }
        versions={
          <RuleVersionsTab
            ruleId={ruleId}
            draft={vm.draft}
            draftContent={
              <SavedRuleDraft rule={rule} advancedEnabled={advancedEnabled} />
            }
            // The source stays reachable for orgs that author rules in code;
            // the graph is what the card opens on. See `RuleVersionBody`.
            source={
              advancedEnabled
                ? {
                    label: "Code",
                    render: (version) => (
                      <AgenticVersionCode
                        ruleId={ruleId}
                        version={version}
                        readOnly={false}
                      />
                    ),
                  }
                : undefined
            }
          />
        }
        advanced={
          <CodegenWorkspace
            config={config}
            vm={vm}
            capabilities={AGENTIC_CAPABILITIES}
            // The page frame above owns the name, the description and the write
            // actions, so the shell must not draw a second header of its own.
            chrome="embedded"
            showBottomSection={false}
            showGateProgress={false}
            recentRuns={
              isAdmin ? <RecentRunsPanel ruleId={ruleId} /> : undefined
            }
            readOnly={false}
            isAiUpdating={isAtlasUpdatingSource}
            disablePersistedActions={disablePersistedActions}
            onSelectGate={openGateInEditor}
            onAskAiFix={recheckDisabled ? undefined : askAtlasToFix}
            onClose={goBack}
            rollbackImpactCopy={agenticRollbackImpactCopy}
            gateRunAbandonedReason={gateRunAbandonedReason}
            gateRunDescribesLiveVersion={gateRunDescribesLiveVersion}
            // Version history is the page's own top-level tab now, so the
            // shell must not draw a second copy of it inside Advanced.
            showVersionHistory={false}
          />
        }
      />
    </>
  );
};

export const AgenticLogicRuleWorkspacePage = ({
  rule,
}: {
  rule: AgenticLogicRuleDto;
}) => {
  const { settled, datasetsFailed, dataSurface } = useAgenticLogicDataSurface();

  // Gate the mount on the data surface being SETTLED, not on it succeeding.
  // An admin's types module is built from the org's dataset keys, so mounting
  // before they land would type `sdk.getDataset()` against an empty union and
  // squiggle an existing rule's calls until the query returned. Gating on
  // success instead would let a failed secondary metadata read block all code
  // editing, which is the worse failure — so on error we mount with the
  // zero-key projection and say so in a banner. A merchant settles as soon as
  // their admin status is known: their projection has no dataset surface.
  if (!settled) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%" }}
      >
        <IgLoading />
      </Stack>
    );
  }

  return (
    // Keyed on ruleId so navigating between rules REMOUNTS the workspace. The
    // shell holds the editor buffer in local state and does not reset it when
    // the entity ref changes, so without this key rule A's unsaved code stays
    // in the buffer and the next save writes it over rule B's draft.
    <RuleWorkspace
      key={rule.id}
      rule={rule}
      dataSurface={dataSurface}
      datasetsFailed={datasetsFailed}
    />
  );
};

/** Shown while the route resolves without a rule id (should not happen). */
export const AgenticLogicRuleWorkspaceFallback = () => (
  <IgTypography>Rule not found.</IgTypography>
);
